import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { validLocalPhone, composePhone, localPlaceholder } from '../utils/phone';

const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#6B5D48';
const BD = '#E8E0D0';
const BG = '#FAF8F5';
const RED = '#C0392B';

// 完善资料：微信快捷登录用户参拍/充值前强制绑定手机号（唯一+验证码），邮箱选填。
export default function CompleteProfile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const redirect = params.get('redirect') || '/my-center';

  const [countryCode, setCountryCode] = useState('+86');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsCd, setSmsCd] = useState(0);          // 倒计时
  const [sendingSms, setSendingSms] = useState(false);
  const [bindingPhone, setBindingPhone] = useState(false);
  const [changePhone, setChangePhone] = useState(false); // 已绑定后点「更换」展开输入
  const phoneBound = !!(user?.phone && String(user.phone).trim());

  // 邮箱（选填）
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailCd, setEmailCd] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [bindingEmail, setBindingEmail] = useState(false);
  const emailBound = !!(user?.email && String(user.email).trim());

  useEffect(() => {
    if (smsCd <= 0) return;
    const t = setTimeout(() => setSmsCd(smsCd - 1), 1000);
    return () => clearTimeout(t);
  }, [smsCd]);
  useEffect(() => {
    if (emailCd <= 0) return;
    const t = setTimeout(() => setEmailCd(emailCd - 1), 1000);
    return () => clearTimeout(t);
  }, [emailCd]);

  const sendSms = async () => {
    if (!validLocalPhone(countryCode, phone)) return message.error('请输入正确的手机号');
    setSendingSms(true);
    try {
      // phone 统一为存储格式（中国裸号 / 国际 E.164），与绑定提交时的格式一致
      await api.post('/auth/send-sms', { phone: composePhone(countryCode, phone), country_code: countryCode });
      setSmsCd(60);
      message.success('验证码已发送');
    } catch (e) {
      message.error(e.response?.data?.error || '发送失败，请稍后再试');
    } finally { setSendingSms(false); }
  };

  const bindPhone = async () => {
    if (!validLocalPhone(countryCode, phone)) return message.error('请输入正确的手机号');
    if (!smsCode) return message.error('请输入验证码');
    setBindingPhone(true);
    try {
      await api.post('/auth/bind-phone', { phone: composePhone(countryCode, phone), code: smsCode, country_code: countryCode });
      await refreshUser();
      setChangePhone(false); setPhone(''); setSmsCode('');
      message.success('手机号绑定成功');
      navigate(redirect, { replace: true });
    } catch (e) {
      message.error(e.response?.data?.error || '绑定失败');
    } finally { setBindingPhone(false); }
  };

  const sendEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return message.error('请输入正确的邮箱');
    setSendingEmail(true);
    try {
      await api.post('/auth/send-email-code', { email });
      setEmailCd(60);
      message.success('验证码已发送到邮箱');
    } catch (e) {
      message.error(e.response?.data?.error || '发送失败，请稍后再试');
    } finally { setSendingEmail(false); }
  };

  const bindEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return message.error('请输入正确的邮箱');
    if (!emailCode) return message.error('请输入邮箱验证码');
    setBindingEmail(true);
    try {
      await api.post('/auth/bind-email', { email, code: emailCode });
      await refreshUser();
      message.success('邮箱绑定成功');
      setShowEmail(false);
    } catch (e) {
      message.error(e.response?.data?.error || '绑定失败');
    } finally { setBindingEmail(false); }
  };

  const inputStyle = { width: '100%', padding: '11px 12px', border: `1px solid ${BD}`, borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box', background: '#fff' };
  const codeBtn = (loading, cd, onClick) => ({
    flexShrink: 0, padding: '0 14px', height: 44, borderRadius: 8, border: `1px solid ${GOLD}`,
    background: '#fff', color: GOLD, fontSize: 13, fontWeight: 600,
    cursor: (loading || cd > 0) ? 'not-allowed' : 'pointer', opacity: (loading || cd > 0) ? 0.5 : 1, whiteSpace: 'nowrap',
  });

  return (
    <div style={{ minHeight: '100vh', background: BG, padding: '18px 16px 40px', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', fontSize: 20, color: MUTED, cursor: 'pointer', padding: 0 }}>‹</button>
        <span style={{ fontSize: 17, fontWeight: 700, color: INK }}>完善资料</span>
      </div>

      <div style={{ background: 'rgba(184,137,42,.08)', border: `1px solid ${BD}`, borderRadius: 10, padding: '12px 14px', fontSize: 13, color: MUTED, lineHeight: 1.7, marginBottom: 18 }}>
        为保障您的资金与拍品安全、便于客服为您服务，<b style={{ color: INK }}>参拍与充值前需先绑定手机号</b>。手机号仅用于身份识别与重要通知，不会外泄。
      </div>

      {/* 手机号 */}
      <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: INK }}>
        手机号 <span style={{ color: RED }}>*</span>
        {phoneBound && <span style={{ marginLeft: 8, fontSize: 12, color: '#2E8B57', fontWeight: 400 }}>已绑定 {user.phone}</span>}
      </div>
      {phoneBound && !changePhone && (
        <button onClick={() => setChangePhone(true)} style={{ background: 'none', border: `1px solid ${BD}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, color: MUTED, cursor: 'pointer' }}>更换手机号</button>
      )}
      {(!phoneBound || changePhone) && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <CountryCodeSelect value={countryCode} onChange={setCountryCode}
              style={{ border: `1px solid ${BD}`, borderRadius: 8, background: '#fff', padding: '0 8px', fontSize: 14 }} />
            <input style={inputStyle} type="tel" maxLength={11} placeholder={localPlaceholder(countryCode)} value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input style={inputStyle} inputMode="numeric" maxLength={6} placeholder="短信验证码" value={smsCode} onChange={e => setSmsCode(e.target.value.replace(/\D/g, ''))} />
            <button style={codeBtn(sendingSms, smsCd)} disabled={sendingSms || smsCd > 0} onClick={sendSms}>
              {smsCd > 0 ? `${smsCd}s` : '获取验证码'}
            </button>
          </div>
          <button onClick={bindPhone} disabled={bindingPhone} style={{
            width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', background: GOLD, color: '#fff',
            fontSize: 15, fontWeight: 600, cursor: bindingPhone ? 'not-allowed' : 'pointer', opacity: bindingPhone ? 0.6 : 1,
          }}>{bindingPhone ? '绑定中…' : '绑定手机号并继续'}</button>
        </>
      )}

      {/* 邮箱（选填） */}
      <div style={{ marginTop: 28, paddingTop: 18, borderTop: `1px solid ${BD}` }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 8 }}>
          邮箱 <span style={{ fontSize: 12, color: MUTED, fontWeight: 400 }}>（选填，用于接收对账单/中拍通知）</span>
          {emailBound && <span style={{ marginLeft: 8, fontSize: 12, color: '#2E8B57', fontWeight: 400 }}>已绑定 {user.email}</span>}
        </div>
        {!showEmail && (
          <button onClick={() => setShowEmail(true)} style={{ background: 'none', border: `1px solid ${BD}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, color: MUTED, cursor: 'pointer' }}>{emailBound ? '更换邮箱' : '+ 绑定邮箱'}</button>
        )}
        {showEmail && (
          <>
            <input style={{ ...inputStyle, marginBottom: 10 }} type="email" placeholder="请输入邮箱" value={email} onChange={e => setEmail(e.target.value.trim())} />
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input style={inputStyle} inputMode="numeric" maxLength={6} placeholder="邮箱验证码" value={emailCode} onChange={e => setEmailCode(e.target.value.replace(/\D/g, ''))} />
              <button style={codeBtn(sendingEmail, emailCd)} disabled={sendingEmail || emailCd > 0} onClick={sendEmail}>
                {emailCd > 0 ? `${emailCd}s` : '获取验证码'}
              </button>
            </div>
            <button onClick={bindEmail} disabled={bindingEmail} style={{
              width: '100%', padding: '11px 0', borderRadius: 8, border: `1px solid ${GOLD}`, background: '#fff', color: GOLD,
              fontSize: 14, fontWeight: 600, cursor: bindingEmail ? 'not-allowed' : 'pointer', opacity: bindingEmail ? 0.6 : 1,
            }}>{bindingEmail ? '绑定中…' : '绑定邮箱'}</button>
          </>
        )}
      </div>

      {phoneBound && (
        <button onClick={() => navigate(redirect, { replace: true })} style={{
          width: '100%', marginTop: 28, padding: '13px 0', borderRadius: 8, border: 'none', background: GOLD, color: '#fff',
          fontSize: 15, fontWeight: 600, cursor: 'pointer',
        }}>完成</button>
      )}
    </div>
  );
}
